import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getTenantSettings, TenantIdentifier } from "shared/config";
import { Language } from "shared/model/languages";
import { Organisation } from "shared/model/organisations";
import { EnabledPage, FrontendPageEnum } from "shared/model/pages";

import { TenantPage } from "./tenant-page";
import { useApiQuery } from "../api/use-api";
import { HAS_EVENTS, ME_KEY } from "../types/query-keys";
import { useTenantId } from "../util/use-active-tenant-id";

export interface TenantPage extends BareTenantPage {
  page: EnabledPage;
}

export interface BareTenantPage {
  page: Omit<
    EnabledPage,
    "groupsWhichCanSeeThePage" | "supportedLanguages" | "pageLock"
  >;
  tenantId: TenantIdentifier;
}

export const useTenantPages = (): TenantPage[] | undefined => {
  const {
    i18n: { language },
  } = useTranslation();

  const { disease } = useTenantId();
  const { data: diseaseUser } = useApiQuery(
    "backend",
    (api) => api.getMe({ mamaDisease: disease }),
    ME_KEY({ disease }),
  );

  const { data: hasEvents } = useApiQuery(
    "landbot",
    (api) => api.hasEvents({ mamaDisease: disease }),
    HAS_EVENTS(disease),
  );

  const pageLanguageAllowUserToSee = useCallback(
    (lPgae: EnabledPage) => {
      return lPgae.supportedLanguages
        ? lPgae.supportedLanguages?.includes(language as Language)
        : true;
    },
    [language],
  );

  const getTenantPage = useCallback(
    (page: EnabledPage, organisation?: Organisation): TenantPage => ({
      page,
      tenantId: { disease, organisation },
    }),
    [disease],
  );

  const removeTimelineRouteIfEmpty = useCallback(
    (tenantPage: TenantPage[]) => {
      if (!hasEvents?.hasEvents) {
        return tenantPage?.filter(
          (route) => route.page?.route !== FrontendPageEnum.JOURNEY,
        );
      }

      return tenantPage;
    },
    [hasEvents],
  );

  const getPages = useCallback(
    (organisation?: Organisation): TenantPage[] => {
      const tenantSettings = getTenantSettings({ disease, organisation });

      if (!tenantSettings) {
        return [];
      }

      return removeTimelineRouteIfEmpty(
        tenantSettings.enabledPages
          .filter((page) => pageLanguageAllowUserToSee(page))
          .map((page): TenantPage => getTenantPage(page, organisation)),
      );
    },
    [
      disease,
      getTenantPage,
      pageLanguageAllowUserToSee,
      removeTimelineRouteIfEmpty,
    ],
  );

  const diseasePages = useMemo(() => getPages(), [getPages]);

  return useMemo(
    () => diseaseUser && [...diseasePages],
    [diseasePages, diseaseUser],
  );
};
